import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, ArrowLeft, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";

const PLANS = [
  { id: "starter", name: "Starter", price: "₹4,999/mo", desc: "Up to 500 patients · 1 doctor seat" },
  { id: "growth", name: "Growth", price: "₹12,999/mo", desc: "Up to 3,000 patients · 5 seats · WhatsApp triage" },
  { id: "scale", name: "Scale", price: "₹29,999/mo", desc: "Unlimited · Voice EMR · Priority support" },
];

export default function SignupClinic() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    clinic_name: "", contact_name: "", contact_email: "", contact_phone: "",
    address: "", specialties: "", expected_patients_per_month: 500, plan: "growth",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/clinic-signup", form);
      setDone(true);
      toast.success("Request submitted! Projexino team will reach out shortly.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen app-bg flex items-center justify-center p-6">
        <div className="max-w-md text-center solid-panel p-10 tilt-card fade-up">
          <div className="mx-auto h-16 w-16 rounded-2xl bg-gradient-to-br from-[#f37021] to-[#ff9446] flex items-center justify-center text-white text-3xl mb-4 shadow-lg">
            <Sparkles className="h-8 w-8" />
          </div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">You're on the list! 🎉</h1>
          <p className="text-slate-600 mt-2">
            Hi <b>{form.contact_name}</b>, we just sent a payment QR to <b>{form.contact_phone}</b> on WhatsApp.
            Pay using any UPI app and reply <b>DONE</b> — your <b>{form.clinic_name}</b> dashboard will go live within minutes.
          </p>
          <Button onClick={() => navigate("/login")} className="mt-6 bg-[#004a99] hover:bg-[#003b7a] text-white" data-testid="signup-back-login">
            Back to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen app-bg grid lg:grid-cols-5">
      {/* Left brand strip */}
      <div className="hidden lg:flex lg:col-span-2 bg-[#004a99] text-white p-12 flex-col justify-between relative overflow-hidden">
        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-[#f37021]/20 blur-3xl" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-12 w-12 rounded-2xl bg-white/15 ring-1 ring-white/30 flex items-center justify-center">
            <Activity className="h-6 w-6" />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-white/70 font-semibold">Projexino</div>
            <div className="text-2xl font-heading font-bold">Pulse-Agent AI</div>
          </div>
        </div>
        <div className="relative z-10">
          <h1 className="text-4xl font-heading font-bold leading-tight">List your clinic. <span className="text-[#f37021]">Go live in minutes.</span></h1>
          <p className="text-white/80 mt-4 leading-relaxed">
            Submit a request → receive a payment QR on WhatsApp → pay → log in. That's it. No demos, no calls (unless you want one).
          </p>
          <ul className="mt-6 space-y-3 text-white/85">
            <li>✓ AI WhatsApp triage out of the box</li>
            <li>✓ Auto revenue recovery for overdue bills</li>
            <li>✓ Voice → structured PDF prescription</li>
            <li>✓ White-labeled for your clinic brand</li>
          </ul>
        </div>
        <div className="relative z-10 text-xs text-white/60">© Projexino · Modern Clinical Glassmorphism</div>
      </div>

      {/* Right form */}
      <div className="lg:col-span-3 flex items-start justify-center p-6 sm:p-12 overflow-y-auto">
        <div className="w-full max-w-xl">
          <button onClick={() => navigate("/login")} className="text-slate-500 text-sm flex items-center gap-1 mb-4 hover:text-[#004a99]" data-testid="signup-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to login
          </button>
          <h2 className="text-3xl font-heading font-bold tracking-tight">List your clinic</h2>
          <p className="text-slate-500 mt-1">Fill this once. We'll reach out on WhatsApp within 30 minutes.</p>

          <form onSubmit={submit} className="mt-7 space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Clinic name *</Label>
                <Input required value={form.clinic_name} onChange={(e) => set("clinic_name", e.target.value)} placeholder="Apollo Care Clinic" data-testid="signup-clinic-name" />
              </div>
              <div>
                <Label>Specialties</Label>
                <Input value={form.specialties} onChange={(e) => set("specialties", e.target.value)} placeholder="GP, Pediatrics, ENT" data-testid="signup-specialties" />
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Input value={form.address} onChange={(e) => set("address", e.target.value)} placeholder="221B Baker Street, Mumbai" data-testid="signup-address" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Owner / Doctor name *</Label>
                <Input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} placeholder="Dr. Anya Sharma" data-testid="signup-contact-name" />
              </div>
              <div>
                <Label>WhatsApp number *</Label>
                <Input required value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} placeholder="+919811000001" data-testid="signup-contact-phone" />
              </div>
            </div>
            <div>
              <Label>Email *</Label>
              <Input required type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} placeholder="you@clinic.com" data-testid="signup-contact-email" />
            </div>
            <div>
              <Label>Expected patients per month</Label>
              <Input type="number" value={form.expected_patients_per_month} onChange={(e) => set("expected_patients_per_month", parseInt(e.target.value) || 0)} data-testid="signup-patients" />
            </div>

            <div>
              <Label className="mb-2 block">Choose a plan</Label>
              <div className="grid sm:grid-cols-3 gap-3">
                {PLANS.map((p) => (
                  <button type="button" key={p.id} onClick={() => set("plan", p.id)}
                          className={`tilt-card text-left p-3 rounded-xl border-2 transition ${form.plan === p.id ? "border-[#f37021] bg-[#ffefe6]" : "border-slate-200 bg-white hover:border-slate-300"}`}
                          data-testid={`signup-plan-${p.id}`}>
                    <div className="font-heading font-semibold text-slate-900">{p.name}</div>
                    <div className="text-[#004a99] font-bold mt-0.5">{p.price}</div>
                    <div className="text-xs text-slate-500 mt-1">{p.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            <Button type="submit" disabled={busy} className="w-full h-12 bg-[#f37021] hover:bg-[#d9621b] text-white font-semibold text-base" data-testid="signup-submit-btn">
              {busy ? "Submitting…" : "Submit & receive payment QR"}
            </Button>
            <p className="text-xs text-slate-500 text-center">Projexino team will WhatsApp you a UPI payment QR. After payment, your dashboard activates instantly.</p>
          </form>
        </div>
      </div>
    </div>
  );
}
